import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { get, patch, post, tokenStore } from "@/lib/api";
import { setLocale } from "@/i18n";

const AuthContext = createContext(null);

const ROLE_RANK = { viewer: 0, member: 1, admin: 2, owner: 3 };

/**
 * Session state. Auth provider agnostic: `login`/`register` use email+password today; an OAuth flow only
 * needs to call `applySession` with the same token payload (access_token, refresh_token, user, organizations).
 */
export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState(tokenStore.org);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const pickOrg = useCallback((orgs, preferred) => {
    const stored = preferred || tokenStore.org;
    const next = orgs.find((o) => o.id === stored)?.id || orgs[0]?.id || null;
    tokenStore.setOrg(next);
    setOrgId(next);
    return next;
  }, []);

  const applySession = useCallback((data) => {
    tokenStore.set(data);
    setUser(data.user);
    const orgs = data.organizations || [];
    setOrganizations(orgs);
    if (data.user?.locale) setLocale(data.user.locale);
    pickOrg(orgs);
    setSessionExpired(false);
  }, [pickOrg]);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setOrganizations([]);
    setOrgId(null);
    queryClient.clear();
  }, [queryClient]);

  const logout = useCallback(async () => {
    const refresh = tokenStore.refresh;
    clearSession();
    if (refresh) {
      try {
        await post("/auth/logout", { refresh_token: refresh });
      } catch (e) {
        /* session already invalid server-side */
      }
    }
  }, [clearSession]);

  useEffect(() => {
    const handler = () => {
      clearSession();
      setSessionExpired(true);
    };
    window.addEventListener("og:logout", handler);
    return () => window.removeEventListener("og:logout", handler);
  }, [clearSession]);

  useEffect(() => {
    (async () => {
      if (!tokenStore.access) {
        setLoading(false);
        return;
      }
      try {
        const me = await get("/auth/me");
        applySession(me);
      } catch (e) {
        tokenStore.clear();
      } finally {
        setLoading(false);
      }
    })();
  }, [applySession]);

  const login = useCallback(async (email, password) => {
    const data = await post("/auth/login", { email, password });
    applySession(data);
    return data;
  }, [applySession]);

  const register = useCallback(async (payload) => {
    const data = await post("/auth/register", payload);
    applySession(data);
    return data;
  }, [applySession]);

  const refreshOrganizations = useCallback(async (preferredId) => {
    const me = await get("/auth/me");
    setUser(me.user);
    const orgs = me.organizations || [];
    setOrganizations(orgs);
    pickOrg(orgs, preferredId);
    return orgs;
  }, [pickOrg]);

  const switchOrganization = useCallback((id) => {
    tokenStore.setOrg(id);
    setOrgId(id);
    queryClient.clear();
  }, [queryClient]);

  const updateProfile = useCallback(async (payload) => {
    const updated = await patch("/auth/me", payload);
    setUser(updated);
    if (payload.locale) setLocale(payload.locale);
    return updated;
  }, []);

  const patchCurrentOrg = useCallback((changes) => {
    setOrganizations((prev) => prev.map((o) => (o.id === (changes.id || orgId) ? { ...o, ...changes } : o)));
  }, [orgId]);

  const currentOrg = useMemo(() => organizations.find((o) => o.id === orgId) || null, [organizations, orgId]);
  const isPlatformAdmin = user?.is_platform_admin === true;

  const hasRole = useCallback((minimum) => {
    if (user?.is_platform_admin) return true;
    const role = currentOrg?.role;
    return (ROLE_RANK[role] ?? -1) >= (ROLE_RANK[minimum] ?? 99);
  }, [user, currentOrg]);

  const value = useMemo(
    () => ({ user, organizations, orgId, currentOrg, isPlatformAdmin, loading, sessionExpired, login, register, logout, switchOrganization, refreshOrganizations, updateProfile, patchCurrentOrg, hasRole, setUser, applySession }),
    [user, organizations, orgId, currentOrg, isPlatformAdmin, loading, sessionExpired, login, register, logout, switchOrganization, refreshOrganizations, updateProfile, patchCurrentOrg, hasRole, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
